import { apiErrorField } from '@agos/api-client'
import {
  getGetOutgoingInvoicesQueryKey,
  useGetInvoiceCompanies,
  useGetOutgoingInvoices,
  useGetOutgoingInvoicesInvoiceId,
  usePostOutgoingInvoices,
  usePostOutgoingInvoicesInvoiceIdCancel,
  usePostOutgoingInvoicesInvoiceIdMarkPaid,
} from '@agos/api-client/invoicing'
import { useGetVatCategories } from '@agos/api-client/menu'
import {
  useGetCentralBranding,
  useGetSettingsBasic,
  useGetSettingsBilling,
} from '@agos/api-client/platform'
import { T, tt, useModalFocus } from '@agos/shared'
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  cn,
  type Column,
  ConfirmDialog,
  DataTable,
  FilterBar,
  FilterSelect,
  IconButton,
  PageHeader,
  ReadFailedBlock,
  Spinner,
  StatCard,
  StatusBadge,
  useToast,
} from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import { Printer,
  AlertCircle,
  BadgeEuro,
  CheckCircle2,
  Clock,
  Eye,
  FileText,
  Plus,
  Trash2,
  X,
} from 'lucide-react'
import { useMemo, useRef, useState, type ReactNode } from 'react'

import {
  addDays,
  byAttention,
  canIssue,
  customerProblems,
  daysOverdue,
  draftProblems,
  emptyLine,
  filterInvoices,
  formatCents,
  formatQty,
  formatUnitPrice,
  invoiceTotals,
  isMixedRate,
  lineOf,
  lineTotals,
  listTotals,
  statusColor,
  statusLabel,
  totalsAgree,
  variableSymbolFor,
  vatBands,
  type CustomerProblem,
  type DraftProblem,
  type InvoiceDetail,
  type InvoiceRow,
  type LineForm,
  invoiceHtml,
  issuerProblems,
  type InvoiceIssuer,
} from './outgoing-invoice'

/**
 * `/outgoing-invoices` — the invoices this kitchen sends out. Tax documents.
 *
 * **FB-088.** Legacy computes each line with `+(x).toFixed(2)` and then sums the rounded
 * floats, so six lines of 0,10 € total `0.6000000000000001` — and that float is what gets
 * posted and stored as the invoice's total. Everything here is integer cents (§14.7), and
 * the gross is `net + vat` rather than a third independent sum, so the header cannot
 * contradict its own lines.
 *
 * **FB-089.** The printed invoice shows one *Cena bez DPH / DPH / Spolu* triple and no
 * per-rate recapitulation. On a single-rate invoice that is complete; this kitchen bills
 * 5 % for food eaten in beside 19 % for a delivery, and a mixed invoice needs the split.
 *
 * Ported from legacy `pages/outgoing-invoices.tsx`.
 */

export function OutgoingInvoicesPage() {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [now] = useState(() => Date.now())
  const [status, setStatus] = useState('all')
  const [search, setSearch] = useState('')
  const [openId, setOpenId] = useState<number | null>(null)
  const [creating, setCreating] = useState(false)
  const [marking, setMarking] = useState<InvoiceRow | null>(null)
  const [deleting, setDeleting] = useState<InvoiceRow | null>(null)

  const { data, isLoading, isError, refetch } = useGetOutgoingInvoices()
  const invoices = useMemo<InvoiceRow[]>(() => data ?? [], [data])
  const shown = useMemo(
    () => byAttention(filterInvoices(invoices, { status, search }), now),
    [invoices, status, search, now],
  )
  const totals = listTotals(invoices, now)

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: getGetOutgoingInvoicesQueryKey() })
  }
  const onError = (error: unknown) =>
    toast({
      title: tt('Chyba'),
      description: messageOf(error) ?? tt('Operácia sa nepodarila.'),
      variant: 'destructive',
    })

  const markPaid = usePostOutgoingInvoicesInvoiceIdMarkPaid({
    mutation: {
      onSuccess: () => {
        setMarking(null)
        invalidate()
      },
      onError,
    },
  })
  /**
   * A tax document is **cancelled**, not deleted — legacy's button calls
   * `DELETE /outgoing-invoices/{id}`, which would leave a hole in the numbering. AGOS
   * offers both verbs and this uses the one that keeps the series intact.
   */
  const cancel = usePostOutgoingInvoicesInvoiceIdCancel({
    mutation: {
      onSuccess: () => {
        setDeleting(null)
        invalidate()
      },
      onError,
    },
  })

  const columns: Column<InvoiceRow>[] = useMemo(
    () => [
      {
        key: 'invoiceNumber',
        header: tt('Číslo'),
        sortable: true,
        sortValue: (invoice) => invoice.invoiceNumber ?? '',
        render: (invoice) => (
          <div className="min-w-0">
            <p className="font-mono text-xs font-bold text-primary">{invoice.invoiceNumber}</p>
            <p className="text-[11px] text-muted-foreground">
              VS {variableSymbolFor(invoice.invoiceNumber ?? '')}
            </p>
          </div>
        ),
      },
      {
        key: 'customerName',
        header: tt('Odberateľ'),
        sortable: true,
        sortValue: (invoice) => invoice.customerName ?? '',
        render: (invoice) => (
          <span className="font-semibold text-sm truncate">{invoice.customerName}</span>
        ),
      },
      {
        key: 'issueDate',
        header: tt('Vystavená'),
        sortable: true,
        sortValue: (invoice) => invoice.issueDate ?? '',
        render: (invoice) => (
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            {invoice.issueDate}
          </span>
        ),
      },
      {
        key: 'dueDate',
        header: tt('Splatnosť'),
        sortable: true,
        sortValue: (invoice) => invoice.dueDate ?? '',
        render: (invoice) => {
          const late = daysOverdue(invoice, now)
          return (
            <span className="text-xs whitespace-nowrap">
              {invoice.dueDate}
              {late !== null && (
                <span className="block text-[11px] text-red-500 font-semibold">
                  {/* Legacy shows the date and nothing about whether it has passed. */}
                  {late} {tt('dní po termíne')}
                </span>
              )}
            </span>
          )
        },
      },
      {
        key: 'totalGross',
        header: tt('Suma'),
        sortable: true,
        sortValue: (invoice) => parseCents(invoice.totalGross),
        className: 'text-right',
        headerClassName: 'text-right',
        render: (invoice) => (
          <span className="font-bold tabular-nums whitespace-nowrap">
            {formatCents(parseCents(invoice.totalGross))}
          </span>
        ),
      },
      {
        key: 'status',
        header: tt('Stav'),
        sortable: true,
        sortValue: (invoice) => invoice.status ?? '',
        render: (invoice) => (
          <StatusBadge label={statusLabel(invoice.status)} color={statusColor(invoice.status)} />
        ),
      },
      {
        key: 'actions',
        header: '',
        render: (invoice) => (
          <div className="flex items-center gap-1 justify-end">
            <IconButton
              label={`${tt('Otvoriť faktúru')} ${invoice.invoiceNumber ?? ''}`}
              onClick={() => setOpenId(invoice.id ?? null)}
            >
              <Eye className="w-3.5 h-3.5" />
            </IconButton>
            {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
              <IconButton
                label={`${tt('Označiť ako zaplatenú')} ${invoice.invoiceNumber ?? ''}`}
                onClick={() => setMarking(invoice)}
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
              </IconButton>
            )}
            <IconButton
              label={`${tt('Zrušiť faktúru')} ${invoice.invoiceNumber ?? ''}`}
              tone="red"
              onClick={() => setDeleting(invoice)}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </IconButton>
          </div>
        ),
      },
    ],
    [now],
  )

  return (
    <div className="space-y-5">
      <PageHeader
        title={tt('Vydané faktúry')}
        description={tt('Faktúry, ktoré reštaurácia posiela odberateľom')}
        icon={<FileText className="w-5 h-5" />}
        actions={
          <button
            type="button"
            onClick={() => setCreating(true)}
            className={cn('flex items-center gap-2 px-4 py-2 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
          >
            <Plus className="w-4 h-4" />
            <T>Nová faktúra</T>
          </button>
        }
      />

      {/* FB-819, §9.540 - *Po termíne* is `color={overdueCount > 0 ? 'red' : 'emerald'}`, so a failed read renders
          **0 in green** on the card that says who has not paid the franchise, and the red *N faktúr je po termíne
          splatnosti* line disappears with it. The styling is the claim - §9.530's shape, on money owed to them. */}
      {isError ? (
        <ReadFailedBlock
          marker="outgoing"
          title={tt('Vydané faktúry sa nepodarilo načítať')}
          onRetry={() => void refetch()}
        >
          <T>Čísla preto nezobrazujeme - nula po termíne by neznamenala, že je všetko zaplatené.</T>
        </ReadFailedBlock>
      ) : null}

      {isError ? null : (
      <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-invoice-totals>
        <StatCard label={tt('Faktúr')} value={String(totals.count)} icon={FileText} color="blue" />
        <StatCard
          label={tt('Vyfakturované')}
          value={formatCents(totals.grossCents)}
          icon={BadgeEuro}
          color="violet"
        />
        <StatCard
          label={tt('Nezaplatené')}
          value={formatCents(totals.unpaidCents)}
          icon={Clock}
          color={totals.unpaidCents > 0 ? 'amber' : 'emerald'}
        />
        <StatCard
          label={tt('Po termíne')}
          value={String(totals.overdueCount)}
          icon={AlertCircle}
          color={totals.overdueCount > 0 ? 'red' : 'emerald'}
          sub={totals.overdueCount > 0 ? formatCents(totals.overdueCents) : undefined}
        />
      </div>

      {totals.overdueCount > 0 && (
        <p className="flex items-start gap-1.5 text-xs text-red-500" data-overdue-warning>
          <AlertCircle className="w-4 h-4 shrink-0 mt-px" />
          <span>
            <strong>{totals.overdueCount}</strong>{' '}
            <T>faktúr je po termíne splatnosti, spolu za</T>{' '}
            <strong>{formatCents(totals.overdueCents)}</strong>.{' '}
            <T>Ukazuje aj to, či termín už uplynul - nielen samotný dátum.</T>
          </span>
        </p>
      )}

      </>
      )}

      <FilterBar
        search={{
          value: search,
          onChange: setSearch,
          placeholder: tt('Hľadať číslo, odberateľa, poznámku...'),
        }}
        filters={
          <FilterSelect
            value={status}
            label={tt('Stav faktúry')}
            onChange={setStatus}
            options={[
              { value: 'all', label: tt('Všetky') },
              { value: 'draft', label: tt('Koncepty') },
              { value: 'sent', label: tt('Odoslané') },
              { value: 'paid', label: tt('Zaplatené') },
              { value: 'cancelled', label: tt('Zrušené') },
            ]}
          />
        }
      />

      {isError || isLoading ? (
        isError ? null : (
        <div className="flex items-center justify-center py-16">
          <Spinner />
        </div>
        )
      ) : (
        <div data-invoices>
          <DataTable<InvoiceRow>
            columns={columns}
            data={shown}
            rowKey={(invoice) => invoice.id ?? 0}
            pageSize={25}
            compact
            emptyMessage={tt('Žiadne faktúry')}
            emptyIcon={<FileText className="w-10 h-10 mb-3 opacity-30" />}
          />
        </div>
      )}

      {openId !== null && <InvoiceDialog invoiceId={openId} onClose={() => setOpenId(null)} />}
      {creating && <DraftDialog onClose={() => setCreating(false)} />}

      <ConfirmDialog
        open={marking !== null}
        onClose={() => setMarking(null)}
        onConfirm={() => marking?.id !== undefined && markPaid.mutate({ invoiceId: marking.id })}
        title={`${tt('Označiť')} ${marking?.invoiceNumber ?? ''} ${tt('ako zaplatenú?')}`}
        // Bookkeeping, like the reorder draft's mark-sent: no money moves.
        description={tt('Toto len zapíše, že platba prišla - samo sa nič nevyberá ani neposiela.')}
        confirmLabel={tt('Označiť')}
        loading={markPaid.isPending}
      />

      <ConfirmDialog
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting?.id !== undefined && cancel.mutate({ invoiceId: deleting.id })}
        title={`${tt('Zrušiť faktúru')} ${deleting?.invoiceNumber ?? ''}?`}
        description={tt(
          'Vydaná faktúra je daňový doklad. Zrušením zostane v číselnej rade so stavom „Zrušená", aby v číslovaní nevznikla diera.',
        )}
        confirmLabel={tt('Zrušiť faktúru')}
        variant="danger"
        loading={cancel.isPending}
      />
    </div>
  )
}

function InvoiceDialog({ invoiceId, onClose }: { invoiceId: number; onClose: () => void }) {
  const { toast: showToast } = useToast()
  const { data, isLoading } = useGetOutgoingInvoicesInvoiceId(invoiceId)
  // §9.137 — who is issuing it, as far as the manager can know. No endpoint serves the franchise's
  // tax identifiers, which is why `issuerProblems` says so on screen.
  const { data: brandingData } = useGetCentralBranding()
  const { data: basicData } = useGetSettingsBasic()
  // §9.153 — the owner's answer to `OWNER-QUESTIONS.md` §6: the identifiers come from *Nastavenia →
  // Fakturácia*, which is this section. It already held all four and nothing read them.
  const { data: billingData } = useGetSettingsBilling()
  const billing = (billingData ?? {}) as {
    ico?: string
    dic?: string
    icDph?: string
    iban?: string
  }
  const issuer: InvoiceIssuer = {
    name: (brandingData as { displayName?: string; name?: string } | undefined)?.displayName
      ?? (brandingData as { name?: string } | undefined)?.name,
    address: (basicData as { depotAddress?: string } | undefined)?.depotAddress,
    ico: billing.ico,
    dic: billing.dic,
    icDph: billing.icDph,
    iban: billing.iban,
  }
  const missing = issuerProblems(issuer)
  // Assigned rather than asserted: `GET /outgoing-invoices/{id}` describes itself now
  // (BLOCKED #4), so the compiler checks this against the schema instead of being told.
  const invoice: InvoiceDetail = data ?? {}
  const lines = invoice.lines ?? []
  const totals = invoiceTotals(lines)
  const bands = vatBands(lines)
  const problems = customerProblems(invoice)
  const agrees = totalsAgree(invoice)

  // §9.137 — the sheet the accountant files. Opened into a window and printed, as legacy does; a
  // blocked pop-up is said out loud rather than leaving somebody clicking a button that does nothing.
  const printSheet = () => {
    const sheet = window.open('', '_blank')
    if (sheet === null) {
      showToast({
        variant: 'destructive',
        title: tt('Nepodarilo sa otvoriť náhľad'),
        description: tt('Povoľte vyskakovacie okná a skúste znova.'),
      })
      return
    }
    sheet.document.write(invoiceHtml(invoice, issuer))
    sheet.document.close()
    sheet.focus()
    sheet.print()
  }

  return (
    <Modal title={invoice.invoiceNumber ?? tt('Faktúra')} onClose={onClose} wide>
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Spinner />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
            <div className="space-y-0.5">
              <p className="font-bold text-muted-foreground uppercase tracking-wider">
                <T>Odberateľ</T>
              </p>
              <p className="text-sm font-semibold">{invoice.customerName}</p>
              {invoice.customerAddress && <p className="text-muted-foreground">{invoice.customerAddress}</p>}
              <p className="text-muted-foreground">
                {invoice.customerIco ? `IČO ${invoice.customerIco}` : ''}
                {invoice.customerDic ? ` · DIČ ${invoice.customerDic}` : ''}
                {invoice.customerIcDph ? ` · IČ DPH ${invoice.customerIcDph}` : ''}
              </p>
            </div>
            <div className="space-y-0.5">
              <p className="font-bold text-muted-foreground uppercase tracking-wider">
                <T>Platba</T>
              </p>
              <p className="text-muted-foreground">
                <T>Vystavená</T> {invoice.issueDate} · <T>splatná</T> {invoice.dueDate}
              </p>
              <p className="text-muted-foreground">
                VS {invoice.variableSymbol ?? variableSymbolFor(invoice.invoiceNumber ?? '')}
                {invoice.constantSymbol ? ` · KS ${invoice.constantSymbol}` : ''}
              </p>
              <StatusBadge label={statusLabel(invoice.status)} color={statusColor(invoice.status)} />
            </div>
          </div>

          {problems.length > 0 && (
            <div className="space-y-1" data-customer-problems>
              {problems.map((problem) => (
                <p
                  key={problem.kind}
                  className="flex items-start gap-1.5 text-xs text-amber-500 leading-relaxed"
                >
                  <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-px" />
                  <CustomerProblemText problem={problem} />
                </p>
              ))}
            </div>
          )}

          {!agrees && (
            <p className="flex items-start gap-1.5 text-xs text-red-500" data-totals-disagree>
              <AlertCircle className="w-4 h-4 shrink-0 mt-px" />
              {/* A tax document whose header disagrees with its own lines. */}
              <span>
                <T>Uložené súčty nesúhlasia s riadkami - riadky dávajú</T>{' '}
                <strong>{formatCents(totals.grossCents)}</strong>.
              </span>
            </p>
          )}

          <div className="overflow-x-auto" data-invoice-lines>
            <table className="w-full text-xs">
              <thead className="text-muted-foreground uppercase tracking-wider">
                <tr>
                  <th className="text-left py-1.5">{tt('Položka')}</th>
                  <th className="text-right py-1.5">{tt('Množstvo')}</th>
                  <th className="text-right py-1.5">{tt('Cena/j.')}</th>
                  <th className="text-right py-1.5">DPH</th>
                  <th className="text-right py-1.5">{tt('Spolu')}</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line, index) => {
                  const line_ = lineTotals(line)
                  return (
                    <tr key={line.id ?? index} className="border-t border-border">
                      <td className="py-1.5">{line.itemName}</td>
                      <td className="py-1.5 text-right tabular-nums">
                        {formatQty(line.qty)} {line.unit}
                      </td>
                      <td className="py-1.5 text-right tabular-nums">
                        {formatUnitPrice(line.unitPrice)}
                      </td>
                      <td className="py-1.5 text-right tabular-nums">{line.vatRate} %</td>
                      <td className="py-1.5 text-right tabular-nums font-bold">
                        {formatCents(line_.grossCents)}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {/* FB-089 - the per-rate recapitulation a Slovak invoice has to carry. */}
          <div className="rounded-xl border border-border bg-muted/30 p-3 space-y-1.5" data-vat-bands>
            <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">
              <T>Rekapitulácia DPH</T>
              {isMixedRate(lines) && (
                <span className="ml-1.5 font-normal normal-case">
                  ({bands.length} {tt('sadzby')})
                </span>
              )}
            </p>
            {bands.map((band) => (
              <div key={band.rate} className="flex justify-between text-xs tabular-nums">
                <span className="text-muted-foreground">
                  {band.rate} % - <T>základ</T> {formatCents(band.netCents)}
                </span>
                <span>{formatCents(band.vatCents)}</span>
              </div>
            ))}
            <div className="flex justify-between text-xs tabular-nums pt-1 border-t border-border">
              <span className="text-muted-foreground">
                <T>Základ spolu</T>
              </span>
              <span>{formatCents(totals.netCents)}</span>
            </div>
            <div className="flex justify-between text-sm font-bold tabular-nums">
              <span>
                <T>Na úhradu</T>
              </span>
              <span className="text-primary">{formatCents(totals.grossCents)}</span>
            </div>
          </div>

          {invoice.notes && <p className="text-xs text-muted-foreground">{invoice.notes}</p>}

          {/* §9.137 - the sheet the accountant files. Legacy has this and AGOS had no print at all. */}
          <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-border">
            <button
              type="button"
              data-print-invoice
              onClick={printSheet}
              className={cn('flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm', BUTTON_PRIMARY)}
            >
              <Printer className="w-4 h-4" /> <T>Vytlačiť / PDF</T>
            </button>
            {missing.length > 0 && (
              // Said here rather than printed on the document: a customer's invoice is not the place
              // for a note to the restaurant. A Slovak invoice must identify both parties and no
              // endpoint serves the issuer's — `OWNER-QUESTIONS.md` §6.
              <p className="text-[11px] text-amber-600 dark:text-amber-400" data-issuer-missing>
                <T>Na doklade chýba</T>: {missing.join(', ')}
              </p>
            )}
          </div>
        </>
      )}
    </Modal>
  )
}

function DraftDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { data: categoryData } = useGetVatCategories()

  const rates = useMemo(() => {
    // `as VatCategoryOption[]` until 2026-08-03, against a local four-field copy of the row.
    // `/vat-categories` describes itself now (BLOCKED #4), so this is the real thing.
    const categories = categoryData ?? []
    return categories
      .filter((category) => category.isActive !== false)
      .map((category) => ({
        value: String(Number(category.rate ?? 0)),
        label: `${Number(category.rate ?? 0)} % - ${category.name ?? category.letter ?? ''}`,
      }))
      .sort((left, right) => Number(left.value) - Number(right.value))
  }, [categoryData])

  // FB-070's rule again: the rate comes from the franchise's own categories rather than
  // from a hardcoded 19. The eat-in rate leads, because most of these are food.
  const defaultRate = rates.find((rate) => rate.value === '5')?.value ?? rates[0]?.value ?? '19'

  // Issued today; the term is what the operator picks.
  const [issueDate] = useState(() => new Date().toISOString().slice(0, 10))
  const [customerName, setCustomerName] = useState('')
  const [customerIco, setCustomerIco] = useState('')
  // §9.300 - the owner: "we cannot see DIČ odberateľa, adresa etc". Legacy's form has all four and
  // this one had two; the model, the validation and the printed invoice already carried them.
  const [customerAddress, setCustomerAddress] = useState('')
  const [customerDic, setCustomerDic] = useState('')
  const [customerIcDph, setCustomerIcDph] = useState('')
  const [dueDays, setDueDays] = useState('14')
  const [notes, setNotes] = useState('')
  const [lines, setLines] = useState<LineForm[]>(() => [emptyLine('5')])

  // The enabled companies only: a company switched off in *Externé Firmy → Firmy* is one the
  // restaurant has stopped invoicing, and it stays on the invoices that already name it.
  const { data: companyData } = useGetInvoiceCompanies()
  const companyOptions = useMemo(() => {
    const rows = Array.isArray(companyData) ? companyData : []
    return rows.flatMap((entry) => {
      const row = (entry ?? {}) as Record<string, unknown>
      if (typeof row.id !== 'number' || typeof row.name !== 'string') return []
      const value = (key: string) => {
        const field = row[key]
        return typeof field === 'string' ? field : ''
      }
      return [
        {
          id: row.id,
          name: row.name,
          address: value('address'),
          ico: value('ico'),
          dic: value('dic'),
          icDph: value('icDph'),
        },
      ]
    })
  }, [companyData])

  const parsed = lines.map(lineOf)
  const totals = invoiceTotals(parsed)
  const bands = vatBands(parsed)
  const problems = draftProblems(customerName, lines)

  const create = usePostOutgoingInvoices({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getGetOutgoingInvoicesQueryKey() })
        onClose()
      },
      onError: (error: unknown) =>
        toast({
          title: tt('Chyba'),
          description: messageOf(error) ?? tt('Faktúru sa nepodarilo vystaviť.'),
          variant: 'destructive',
        }),
    },
  })

  const setLine = (index: number, patch: Partial<LineForm>) =>
    setLines((current) =>
      current.map((line, position) => (position === index ? { ...line, ...patch } : line)),
    )

  return (
    <Modal
      title={tt('Nová vydaná faktúra')}
      onClose={onClose}
      wide
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className={cn(BUTTON_SECONDARY, 'flex-1 px-4 py-2.5 rounded-xl text-sm')}
          >
            <T>Zrušiť</T>
          </button>
          <button
            type="button"
            onClick={() =>
              create.mutate({
                data: {
                  customerName: customerName.trim(),
                  customerIco: customerIco.trim() || undefined,
                  customerAddress: customerAddress.trim() || undefined,
                  customerDic: customerDic.trim() || undefined,
                  customerIcDph: customerIcDph.trim() || undefined,
                  issueDate,
                  dueDate: addDays(issueDate, Number(dueDays) || 14),
                  notes: notes.trim() || undefined,
                  // `lineNet` and `lineGross` are *required* by the request, and
                  // `OutgoingInvoiceService` stores them verbatim — `line.setLineNet(
                  // source.lineNet())`. So the client's arithmetic is what lands in the
                  // document, which is why FB-088 matters beyond the screen.
                  lines: parsed.map((line, index) => {
                    const totals = lineTotals(line)
                    return {
                      itemName: line.itemName ?? '',
                      qty: line.qty ?? 0,
                      unit: line.unit ?? 'ks',
                      unitPrice: line.unitPrice ?? 0,
                      vatRate: line.vatRate ?? 0,
                      lineNet: totals.netCents / 100,
                      lineVat: totals.vatCents / 100,
                      lineGross: totals.grossCents / 100,
                      sortOrder: index,
                    }
                  }),
                },
              })
            }
            disabled={create.isPending || !canIssue(problems)}
            className={cn('flex-1 px-4 py-2.5 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
          >
            {create.isPending ? <Spinner className="w-4 h-4 mx-auto" /> : <T>Vystaviť faktúru</T>}
          </button>
        </>
      }
    >
      {/* §9.347, the owner's C2: *"we can have the same dropdown from Firmy we created as in nová
        * faktúra in «Faktúry» where you also select dodávateľ"*. It **fills**, it does not bind - the
        * fields stay editable, because a one-off invoice to a company whose address has changed is
        * ordinary work and because the document keeps its own copy of what was agreed (§10A.135). */}
      <div className="mb-3">
        <Field label={tt('Vybrať firmu')}>
          <select
            data-invoice-company-picker
            value=""
            onChange={(event) => {
              const chosen = companyOptions.find(
                (option) => String(option.id) === event.target.value,
              )
              if (chosen === undefined) return
              setCustomerName(chosen.name)
              setCustomerAddress(chosen.address)
              setCustomerIco(chosen.ico)
              setCustomerDic(chosen.dic)
              setCustomerIcDph(chosen.icDph)
            }}
            className={INPUT}
            aria-label={tt('Vybrať firmu')}
          >
            <option value="">
              {companyOptions.length === 0
                ? tt('Žiadne firmy - pridajte ich v Externé Firmy')
                : tt('Vyplniť z firmy...')}
            </option>
            {companyOptions.map((option) => (
              <option key={option.id} value={String(option.id)}>
                {option.ico === '' ? option.name : `${option.name} · ${option.ico}`}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Field label={tt('Odberateľ')}>
          <input
            value={customerName}
            onChange={(event) => setCustomerName(event.target.value)}
            aria-label={tt('Odberateľ')}
            className={INPUT}
          />
        </Field>
        <Field label={tt('IČO odberateľa')}>
          <input
            value={customerIco}
            onChange={(event) => setCustomerIco(event.target.value)}
            aria-label={tt('IČO odberateľa')}
            className={INPUT}
            placeholder="00309231"
          />
        </Field>
        <Field label={tt('DIČ odberateľa')}>
          <input
            value={customerDic}
            onChange={(event) => setCustomerDic(event.target.value)}
            aria-label={tt('DIČ odberateľa')}
            className={INPUT}
            placeholder="2020317068"
          />
        </Field>
        <Field label={tt('IČ DPH odberateľa')}>
          <input
            value={customerIcDph}
            onChange={(event) => setCustomerIcDph(event.target.value)}
            aria-label={tt('IČ DPH odberateľa')}
            className={INPUT}
            placeholder="SK2020317068"
          />
        </Field>
        <Field label={tt('Adresa odberateľa')}>
          <input
            value={customerAddress}
            onChange={(event) => setCustomerAddress(event.target.value)}
            aria-label={tt('Adresa odberateľa')}
            className={INPUT}
            placeholder={tt('Ulica, mesto, PSČ')}
          />
        </Field>
        <Field label={tt('Splatnosť (dni)')}>
          <input
            value={dueDays}
            onChange={(event) => setDueDays(event.target.value)}
            aria-label={tt('Splatnosť v dňoch')}
            className={INPUT}
          />
        </Field>
      </div>

      {/* §9.300 - the validation §9.153 wrote for these could never fire on a new invoice, because
        * the form had no field to type them into. Each problem is reported on its own, so a bad DIČ
        * and a mismatched IČ DPH are two sentences rather than one vague one. */}
      {customerProblems({
        customerName: 'x',
        customerIco: customerIco.trim() || undefined,
        customerDic: customerDic.trim() || undefined,
        customerIcDph: customerIcDph.trim() || undefined,
      }).map((problem) => (
        <p
          key={problem.kind}
          className="flex items-start gap-1.5 text-xs text-amber-500"
          data-draft-customer-problem={problem.kind}
        >
          <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-px" />
          <CustomerProblemText problem={problem} />
        </p>
      ))}

      <div className="space-y-2" data-draft-lines>
        {lines.map((line, index) => (
          <div key={index} className="grid grid-cols-12 gap-2 items-end">
            <div className="col-span-12 sm:col-span-4">
              <input
                value={line.itemName}
                onChange={(event) => setLine(index, { itemName: event.target.value })}
                aria-label={`${tt('Položka')} ${index + 1}`}
                placeholder={tt('Položka')}
                className={INPUT}
              />
            </div>
            <div className="col-span-3 sm:col-span-2">
              <input
                value={line.qty}
                onChange={(event) => setLine(index, { qty: event.target.value })}
                aria-label={`${tt('Množstvo')} ${index + 1}`}
                className={INPUT}
              />
            </div>
            <div className="col-span-3 sm:col-span-1">
              <input
                value={line.unit}
                onChange={(event) => setLine(index, { unit: event.target.value })}
                aria-label={`${tt('Jednotka')} ${index + 1}`}
                className={INPUT}
              />
            </div>
            <div className="col-span-3 sm:col-span-2">
              <input
                value={line.unitPrice}
                onChange={(event) => setLine(index, { unitPrice: event.target.value })}
                aria-label={`${tt('Cena za jednotku')} ${index + 1}`}
                className={INPUT}
              />
            </div>
            <div className="col-span-2 sm:col-span-2">
              <select
                value={line.vatRate}
                onChange={(event) => setLine(index, { vatRate: event.target.value })}
                aria-label={`DPH ${index + 1}`}
                className={INPUT}
              >
                {rates.length === 0 && <option value="19">19 %</option>}
                {rates.map((rate) => (
                  <option key={rate.value} value={rate.value}>
                    {rate.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-span-1 flex justify-end">
              {lines.length > 1 && (
                <IconButton
                  label={`${tt('Odobrať riadok')} ${index + 1}`}
                  tone="red"
                  onClick={() => setLines((current) => current.filter((_, position) => position !== index))}
                >
                  <X className="w-3.5 h-3.5" />
                </IconButton>
              )}
            </div>
          </div>
        ))}

        <button
          type="button"
          onClick={() => setLines((current) => [...current, emptyLine(defaultRate)])}
          className="flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline"
        >
          <Plus className="w-3.5 h-3.5" />
          <T>Pridať riadok</T>
        </button>
      </div>

      <Field label={tt('Poznámka')}>
        <input
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          aria-label={tt('Poznámka')}
          className={INPUT}
        />
      </Field>

      <div className="rounded-xl border border-border bg-muted/30 p-3 space-y-1.5" data-draft-totals>
        {bands.map((band) => (
          <div key={band.rate} className="flex justify-between text-xs tabular-nums">
            <span className="text-muted-foreground">
              {band.rate} % - <T>základ</T> {formatCents(band.netCents)}
            </span>
            <span>{formatCents(band.vatCents)}</span>
          </div>
        ))}
        <div className="flex justify-between text-sm font-bold tabular-nums pt-1 border-t border-border">
          <span>
            <T>Na úhradu</T>
          </span>
          <span className="text-primary">{formatCents(totals.grossCents)}</span>
        </div>
      </div>

      {problems.length > 0 && (
        <div className="space-y-1" data-draft-problems>
          {problems.map((problem) => (
            <p
              key={`${problem.kind}-${'index' in problem ? problem.index : ''}`}
              className="flex items-start gap-1.5 text-xs text-red-500"
            >
              <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-px" />
              <DraftProblemText problem={problem} />
            </p>
          ))}
        </div>
      )}
    </Modal>
  )
}

function CustomerProblemText({ problem }: { problem: CustomerProblem }) {
  if (problem.kind === 'no-name') return <T>Odberateľ nie je uvedený.</T>
  if (problem.kind === 'ico-checksum') {
    return <T>IČO odberateľa nemá platnú kontrolnú číslicu.</T>
  }
  if (problem.kind === 'dic-length') return <T>DIČ odberateľa má mať desať číslic.</T>
  return (
    <span>
      <T>IČ DPH odberateľa sa nezhoduje s jeho DIČ - malo by byť</T>{' '}
      <strong>{problem.expected}</strong>.
    </span>
  )
}

function DraftProblemText({ problem }: { problem: DraftProblem }) {
  if (problem.kind === 'no-customer') return <T>Odberateľ je povinný.</T>
  if (problem.kind === 'no-lines') return <T>Faktúra musí mať aspoň jeden riadok.</T>
  if (problem.kind === 'no-total') return <T>Faktúra na nulu sa nedá vystaviť.</T>
  if (problem.kind === 'line-no-name') {
    return (
      <span>
        <T>Riadok</T> {problem.index + 1} <T>nemá názov.</T>
      </span>
    )
  }
  return (
    <span>
      <T>Riadok</T> {problem.index + 1} <T>nemá množstvo.</T>
    </span>
  )
}

const INPUT =
  'w-full bg-background border border-border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/20 transition-all'

function Modal({
  title,
  onClose,
  footer,
  wide,
  children,
}: {
  title: string
  onClose: () => void
  footer?: ReactNode
  wide?: boolean
  children: ReactNode
}) {
  /* §9.489, `TODO.md` 3f - this said `aria-modal="true"` and confined nothing. **The close is
     unconditional and that is consistent rather than careless**: the wrapper already calls `onClose` on a
     backdrop click with no guard, so Escape doing the same is the same promise, and this generic `Modal` takes
     no busy flag to guard on - its callers put the submit in `footer`. §9.487's reasoning on a form whose
     submit is a separate button. */
  const panelRef = useRef<HTMLDivElement>(null)
  const { onKeyDown } = useModalFocus(true, panelRef, onClose)

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className={cn(
          'bg-card border border-border rounded-2xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl',
          wide ? 'max-w-3xl' : 'max-w-lg',
        )}
        onClick={(event) => event.stopPropagation()}
        ref={panelRef}
        onKeyDown={onKeyDown}
        data-invoice-dialog
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
          <h3 className="font-bold text-sm truncate">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label={tt('Zatvoriť')}
            data-dialog-cancel
            className="p-1.5 rounded-lg hover:bg-muted transition-colors shrink-0"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-3">{children}</div>
        {footer && <div className="flex gap-3 px-5 py-4 border-t border-border shrink-0">{footer}</div>}
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <span className="text-xs text-muted-foreground uppercase tracking-wide font-medium block">
        {label}
      </span>
      {children}
    </div>
  )
}


/** Local: the list rows arrive as numbers, and money never stays a float here. */
function parseCents(value: InvoiceRow['totalGross']): number {
  const euros = typeof value === 'number' ? value : Number(String(value ?? '0').replace(',', '.'))
  if (!Number.isFinite(euros)) return 0
  return Math.round(euros * 100)
}

/**
 * The server's own sentence, or null - §9.365, FB-652.
 *
 * **This read `record.error ?? record.message` off the thrown value.** `ApiError` has no `error`
 * property - the envelope is in `data` - and its `message` is the **developer** string built by
 * `buildDeveloperMessage`, so every refusal reached the operator as *"HTTP 400 Bad Request: <the
 * real sentence>"*. `apiErrorMessage`'s own javadoc is the rule it broke: the backend's Slovak
 * message *"with no HTTP status glued to the front and no rewording"*.
 *
 * Thirteen files carried this helper. It delegates now rather than being replaced at each call site,
 * so `?? fallback` still reads the same everywhere and there is one reader instead of thirteen.
 */
function messageOf(error: unknown): string | null {
  return apiErrorField(error, 'error')
}
